import { PickerMode, useOnlineStatus } from '@gripp/shared-logic';
import { useEffect, useRef } from 'react';
import { ScrollView, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { MobileIcon } from '../crop-circle/icons';
import { RenderMediaItem } from './renderMediaItem';
import { MediaGalleryProps } from './types';
import { useTypedMedia } from './useTypedMedia';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export const HorizontalMediaGallery = (props: MediaGalleryProps) => {
  const { handleAdd, handleUploadProgress, typedMedia, handleDelete } =
    useTypedMedia(props.media, props.deviceMedia);
  const MediaPicker = props.mediaPicker;
  const scrollRef = useRef<ScrollView>(null);
  // Pickers capture their callbacks when an upload starts, before the parent
  // has re-rendered with the added id, so a failure must reach the latest
  // onMediaDeleted or it removes nothing from the form.
  const onMediaDeletedRef = useRef(props.onMediaDeleted);
  onMediaDeletedRef.current = props.onMediaDeleted;
  const canAddMedia = props.canAddMedia ?? true;
  const { isOnline } = useOnlineStatus();
  const captureLocally = props.captureLocally ?? false;
  // A local capture needs no network; an upload does.
  const canPick = isOnline || captureLocally;
  const pickerMode =
    props.pickerMode === 'picker' ? PickerMode.PICKER : PickerMode.CAMERA;
  // The override only applies while the gallery is empty: deletable
  // thumbnails carry top padding for their remove badge, and the picker's
  // default margin is what keeps it level with them.
  const pickerMarginOverride = props.pickerMarginTop !== undefined &&
    typedMedia.length === 0 && { marginTop: props.pickerMarginTop };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollToEnd({ animated: true });
    }
  }, [typedMedia?.length]);

  return (
    <View style={styles.containerCropCircle}>
      {typedMedia.length > 0 && (
        <ScrollView
          ref={scrollRef}
          horizontal={true}
          showsHorizontalScrollIndicator={true}
          scrollEnabled={true}
          contentContainerStyle={styles.scrollContentCropCircle}
          style={styles.scrollViewCropCircle}
        >
          {typedMedia.map((m) => (
            <View style={styles.mediaItemContainer} key={m.id}>
              <RenderMediaItem
                mediaItem={m}
                key={m.id}
                canDelete={props.canEditMedia}
                deleteMediaItem={(mediaId) =>
                  handleDelete(mediaId, props.onMediaDeleted)
                }
              />
            </View>
          ))}
        </ScrollView>
      )}
      {canAddMedia && !canPick && (
        <View
          style={[
            styles.pickerCropCircle,
            styles.pickerDisabled,
            pickerMarginOverride,
          ]}
          testID="media-add-disabled"
          accessibilityState={{ disabled: true }}
        >
          <MobileIcon size={32} color={Colors.role.textDisabled} />
        </View>
      )}
      {canAddMedia && canPick && (
        <MediaPicker
          workspaceId={props.workspaceId}
          setMedia={(media) => handleAdd(media, props.onMediaAdded)}
          mode={pickerMode}
          setProgress={handleUploadProgress}
          setUploading={props.onUploadingChanged}
          captureLocally={captureLocally}
          // A failed upload must leave the form too, or it submits an id whose
          // bytes never reached storage.
          onUploadError={(mediaId) =>
            handleDelete(mediaId, (id) => onMediaDeletedRef.current?.(id))
          }
          style={[styles.pickerCropCircle, pickerMarginOverride]}
          acceptedMediaTypes={props.acceptedMediaTypes}
          customTrigger={
            <View style={[styles.pickerCropCircle, pickerMarginOverride]}>
              <MobileIcon size={32} color={Colors.role.textStrong} />
            </View>
          }
        />
      )}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  containerCropCircle: {
    flexDirection: 'row',
    display: 'flex',
  },
  scrollViewCropCircle: {
    flexGrow: 0,
    flexShrink: 1,
  },
  scrollContentCropCircle: {
    alignItems: 'center',
  },
  pickerCropCircle: {
    height: 88,
    width: 88,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: role.surfaceHover,
    marginTop: 12,
  },
  pickerDisabled: {
    opacity: 0.5,
  },
  mediaItemContainer: {
    marginLeft: 8,
    marginRight: 2,
  },
}));
